/**
 * renewal 콘텐츠 안의 링크를 어디로 보낼지 결정한다.
 *
 * renewal 은 독립 사이트로도 서비스되므로 마크업에 `https://codepresso.io/home/contact`
 * 같은 절대 URL 이 들어 있다. 그 href 를 우리가 고쳐 쓰지는 않는다 —
 * 고치면 renewal 단독 사이트가 깨지고, "renewal 의 DOM 을 수정하지 않는다"는
 * 통합 원칙에도 어긋난다.
 *
 * 대신 콘텐츠 루트에 건 위임 클릭 핸들러가 이 함수로 목적지를 판정해서,
 * 우리 앱 안의 경로면 라우터로 보낸다. href 는 그대로 남으므로 새 탭이나
 * 우클릭 복사에서는 절대 URL 이 계속 동작한다.
 */

/** 우리 앱이 서빙하는 호스트. 이 호스트로 가는 링크는 내부 라우팅으로 바꾼다. */
export const RENEWAL_INTERNAL_HOSTS = ['codepresso.io', 'www.codepresso.io'] as const;

/**
 * 같은 도메인이지만 우리 앱이 아닌 서브도메인. 외부로 내보낸다.
 * (blog / ai-literacy 는 별도 서비스다)
 */
export const RENEWAL_EXTERNAL_HOSTS = ['blog.codepresso.io', 'ai-literacy.codepresso.io'] as const;

export type RenewalLinkResolution =
    | { kind: 'internal'; path: string }
    | { kind: 'external' }
    /** 링크가 아니거나(앵커·mailto 등) 우리가 관여하지 않는 경우 — 브라우저 기본 동작에 맡긴다. */
    | { kind: 'ignore' };

const NON_NAVIGATION_SCHEMES = ['mailto:', 'tel:', 'javascript:', 'sms:', 'data:'];

/**
 * @param href    `<a>` 의 href 속성 원문
 * @param origin  현재 페이지의 origin (예: `https://codepresso.io`, `http://localhost:3000`)
 */
export function resolveRenewalHref(href: string | null | undefined, origin: string): RenewalLinkResolution {
    const raw = (href ?? '').trim();
    if (!raw) return { kind: 'ignore' };
    // 페이지 내 앵커는 브라우저가 처리한다.
    if (raw.startsWith('#')) return { kind: 'ignore' };
    if (NON_NAVIGATION_SCHEMES.some((scheme) => raw.toLowerCase().startsWith(scheme))) return { kind: 'ignore' };

    let url: URL;
    try {
        url = new URL(raw, origin);
    } catch {
        return { kind: 'ignore' };
    }

    // http(s) 가 아니면 우리가 판단하지 않는다.
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return { kind: 'ignore' };

    const host = url.hostname.toLowerCase();
    const originHost = safeHostname(origin);

    // 현재 보고 있는 호스트와 같으면 내부다. localhost·stage 등 모든 환경에서 성립한다.
    if (originHost && host === originHost) return internal(url);

    if ((RENEWAL_EXTERNAL_HOSTS as readonly string[]).includes(host)) return { kind: 'external' };
    if ((RENEWAL_INTERNAL_HOSTS as readonly string[]).includes(host)) return internal(url);

    return { kind: 'external' };
}

function internal(url: URL): RenewalLinkResolution {
    return { kind: 'internal', path: `${url.pathname}${url.search}${url.hash}` };
}

function safeHostname(origin: string): string {
    try {
        return new URL(origin).hostname.toLowerCase();
    } catch {
        return '';
    }
}
